import * as React from "react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { Loader } from "@/components/ui/loader";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import type { CitationInfo } from "@/modules/chat/chat-ui.types";
import { getColumnLabel, getExcelContent, getWindowBounds, parseCellRef } from "./helpers";

const MAX_VISIBLE_ROWS = 1000;
const MAX_VISIBLE_COLUMNS = 100;

type CellValue = string | number | boolean | Date | null;

interface SheetData {
  name: string;
  rows: CellValue[][];
  columnCount: number;
}

interface Props {
  citationHighlight: CitationInfo | null;
}

export const ExcelViewer: React.FC<Props> = ({ citationHighlight }) => {
  const { currentPdfId } = useCurrentPdf();
  const [sheets, setSheets] = React.useState<SheetData[]>([]);
  const [activeSheetName, setActiveSheetName] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const target = citationHighlight?.spreadsheetTarget;

  React.useEffect(() => {
    const fetchWorkbook = async () => {
      if (!currentPdfId) return;

      setIsLoading(true);
      setError(null);
      setSheets([]);
      setActiveSheetName(null);

      try {
        const response = await getExcelContent(currentPdfId);
        const parsedSheets = await parseWorkbook(response.data);
        setSheets(parsedSheets);
        setActiveSheetName(resolveInitialSheetName(parsedSheets));
      } catch (err) {
        Sentry.captureException(err);
        setError(err instanceof Error ? err.message : "Unable to preview this workbook.");
      } finally {
        setIsLoading(false);
      }
    };

    void fetchWorkbook();
  }, [currentPdfId]);

  React.useEffect(() => {
    if (!target?.sheetName || !sheets.some(sheet => sheet.name === target.sheetName)) {
      return;
    }

    setActiveSheetName(target.sheetName);
  }, [sheets, target?.sheetName]);

  const activeSheet = React.useMemo(
    () => sheets.find(sheet => sheet.name === activeSheetName) ?? sheets[0],
    [activeSheetName, sheets],
  );

  const targetPosition = React.useMemo(() => {
    if (!target || activeSheet?.name !== target.sheetName) return null;
    return parseCellRef(target.cellRef);
  }, [activeSheet?.name, target]);

  const windowBounds = React.useMemo(() => {
    if (!activeSheet) return null;

    return getWindowBounds({
      rowCount: activeSheet.rows.length,
      columnCount: activeSheet.columnCount,
      maxRows: MAX_VISIBLE_ROWS,
      maxColumns: MAX_VISIBLE_COLUMNS,
      target: targetPosition,
    });
  }, [activeSheet, targetPosition]);

  React.useEffect(() => {
    if (!target || activeSheet?.name !== target.sheetName) return;

    const cell = scrollContainerRef.current?.querySelector<HTMLElement>(
      `[data-cell-ref="${target.cellRef.toUpperCase()}"]`,
    );
    cell?.scrollIntoView({ block: "center", inline: "center" });
  }, [activeSheet?.name, target, windowBounds]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div className="flex h-full items-center justify-center px-6 text-center text-sm text-red-500">{error}</div>;
  }

  if (!currentPdfId) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        Select a citation to view the source workbook.
      </div>
    );
  }

  if (!activeSheet || !windowBounds) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
        This workbook does not contain previewable cells.
      </div>
    );
  }

  const visibleColumnIndexes = Array.from(
    { length: windowBounds.columnEnd - windowBounds.columnStart },
    (_, index) => windowBounds.columnStart + index,
  );
  const isLimited = activeSheet.rows.length > MAX_VISIBLE_ROWS || activeSheet.columnCount > MAX_VISIBLE_COLUMNS;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
      <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b px-3 py-2">
        {sheets.map(sheet => (
          <button
            key={sheet.name}
            type="button"
            onClick={() => setActiveSheetName(sheet.name)}
            className={clsx("h-8 shrink-0 rounded border px-3 text-xs font-medium transition-colors", {
              "border-midnight-blue-900 bg-midnight-blue-900 text-white": sheet.name === activeSheet.name,
              "border-gray-200 bg-white text-gray-700 hover:bg-gray-50": sheet.name !== activeSheet.name,
            })}
          >
            {sheet.name}
          </button>
        ))}
      </div>

      {!target && (
        <div className="shrink-0 border-b bg-gray-50 px-4 py-2 text-xs text-muted-foreground">
          No cell target is available for this citation.
        </div>
      )}
      {target && activeSheet.name !== target.sheetName && (
        <div className="shrink-0 border-b bg-gray-50 px-4 py-2 text-xs text-muted-foreground">
          Target cell is not available in this sheet.
        </div>
      )}
      {isLimited && (
        <div className="shrink-0 border-b bg-gray-50 px-4 py-2 text-xs text-muted-foreground">
          Preview is limited to a window of {MAX_VISIBLE_ROWS} rows and {MAX_VISIBLE_COLUMNS} columns.
        </div>
      )}

      <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-auto">
        <table className="border-separate border-spacing-0 text-left text-xs">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-20 h-8 min-w-12 border-b border-r bg-gray-100 px-2 text-center font-medium text-gray-500" />
              {visibleColumnIndexes.map(columnIndex => (
                <th
                  key={columnIndex}
                  className="sticky top-0 z-10 h-8 min-w-32 max-w-64 border-b border-r bg-gray-100 px-2 text-center font-medium text-gray-600"
                >
                  {getColumnLabel(columnIndex)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeSheet.rows.slice(windowBounds.rowStart, windowBounds.rowEnd).map((row, rowOffset) => {
              const rowIndex = windowBounds.rowStart + rowOffset;
              return (
                <tr key={rowIndex}>
                  <th className="sticky left-0 z-10 h-8 min-w-12 border-b border-r bg-gray-100 px-2 text-right font-medium text-gray-500">
                    {rowIndex + 1}
                  </th>
                  {visibleColumnIndexes.map(columnIndex => {
                    const cellRef = `${getColumnLabel(columnIndex)}${rowIndex + 1}`;
                    const isTargetCell =
                      activeSheet.name === target?.sheetName && cellRef.toUpperCase() === target.cellRef.toUpperCase();

                    return (
                      <td
                        key={cellRef}
                        data-cell-ref={cellRef}
                        className={clsx(
                          "h-8 min-w-32 max-w-64 truncate border-b border-r px-2 align-middle text-gray-900",
                          {
                            "bg-brand-gold-100 font-semibold ring-2 ring-inset ring-brand-gold-500": isTargetCell,
                            "bg-white": !isTargetCell,
                          },
                        )}
                        title={formatCellValue(row[columnIndex])}
                      >
                        {formatCellValue(row[columnIndex])}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const parseWorkbook = async (blob: Blob): Promise<SheetData[]> => {
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await blob.arrayBuffer(), { type: "array", cellDates: true });

  return workbook.SheetNames.flatMap(sheetName => {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return [];

    const rows = XLSX.utils.sheet_to_json<CellValue[]>(sheet, {
      header: 1,
      blankrows: true,
      defval: "",
      raw: false,
    });
    const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);

    return [
      {
        name: sheetName,
        rows,
        columnCount,
      },
    ];
  });
};

const resolveInitialSheetName = (sheets: SheetData[], targetSheetName?: string): string | null => {
  if (targetSheetName && sheets.some(sheet => sheet.name === targetSheetName)) {
    return targetSheetName;
  }
  return sheets[0]?.name ?? null;
};

const formatCellValue = (value: CellValue | undefined): string => {
  if (value === undefined || value === null) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
};
